import React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Chip,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon, Notes as NotesIcon } from '@mui/icons-material';
import { formatDateLocal } from '../../../../utils/formatters';
import type { Info360Nota } from '../types';

interface Props {
  notas: Info360Nota[];
  loading: boolean;
  error?: string | null;
}

const Info360NotasBloque: React.FC<Props> = ({ notas, loading, error }) => {
  if (loading) {
    return (
      <Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
          Notas operativas y comerciales
        </Typography>
        <Stack spacing={1}>
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} variant="rounded" height={48} />
          ))}
        </Stack>
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
          Notas operativas y comerciales
        </Typography>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
        Notas operativas y comerciales
      </Typography>

      {notas.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No hay notas disponibles.
        </Typography>
      ) : (
        <Stack spacing={0.5}>
          {notas.map((nota, index) => (
            <Accordion
              key={nota.id ?? index}
              disableGutters
              elevation={0}
              sx={{
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                '&::before': { display: 'none' },
                '&:not(:last-child)': { mb: 0 },
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                  <NotesIcon fontSize="small" color="action" />
                  <Typography variant="body2" noWrap>
                    {nota.contenido
                      ? nota.contenido.length > 80
                        ? `${nota.contenido.slice(0, 80)}…`
                        : nota.contenido
                      : 'Nota'}
                  </Typography>
                  {nota.tipo ? (
                    <Chip
                      size="small"
                      label={nota.tipo}
                      variant="outlined"
                      color={nota.tipo === 'COMERCIAL' ? 'primary' : 'default'}
                    />
                  ) : null}
                  {nota.fecha ? (
                    <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                      {formatDateLocal(nota.fecha)}
                    </Typography>
                  ) : null}
                </Stack>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {nota.contenido}
                </Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Stack>
      )}
    </Box>
  );
};

export default Info360NotasBloque;
